// Where a booking is, in coordinates.
//
// CommonJS on purpose so scripts/check-geo.js can require it with no
// transpiler, the same arrangement bookingRules.js uses. Pure: no Firestore, no
// network, no Vue.
//
// The lookup tables come from src/geoPoints.json, generated from
// doc/th-address.json by scripts/build-geo-points.js. Re-run that after editing
// the address data, then `npm run test:geo`. geoPointsWide.json (every
// province) is merged in by addPoints, for the planner only.
//
// ---------------------------------------------------------------------------
// Precision is part of the answer
// ---------------------------------------------------------------------------
//
// Every resolve returns HOW it was resolved, not just where. A booking with a
// pasted Google Maps pin and one that resolved only as far as its zipcode are
// both "placed", but only one of them can be trusted for a range check on a
// vehicle with 17 km of headroom. Callers that care must look at `precision`:
//
//   'exact'       the booking's own `geo` field -- a real pin, metres
//   'subdistrict' the ตำบล centroid -- the normal case, ~1-3 km in town
//   'district'    the อำเภอ/เขต centroid -- median 3.9 km, 90th pct 8.9 km
//   'zipcode'     the mean of every ตำบล sharing that code -- can be very
//                 coarse: 73000 spans 25 ตำบล, 74000 spans 18
//   null          could not be placed at all
//
// A 'zipcode' answer is a hint for clustering, never grounds for deciding a
// vehicle can make it home. See serviceArea.js, which reaches the same
// conclusion from the other direction: a zipcode that straddles served and
// unserved ตำบล is genuinely undecidable, and it returns 'unknown' rather than
// guess.

const POINTS = require('../geoPoints.json')
// Only the free-text address parser, which is policy-free. serviceArea imports
// nothing itself, so this adds no weight to a module the planner loads for
// every job.
const parseAddress = require('./serviceArea').parseAddress

// "ตำบล,อำเภอ" -> the full keys of POINTS.subdistricts that match, and
// "อำเภอ" -> the full keys of POINTS.districts. An address written the Thai way
// names the ตำบล and the อำเภอ but usually not both halves of the key this
// module indexes by, so the province has to be recovered by lookup. Built once.
const BY_SUBDT = {}
const BY_DISTRICT = {}
Object.keys(POINTS.subdistricts).forEach(key => {
  const parts = key.split(',')
  const short = parts[0] + ',' + parts[1]
  ;(BY_SUBDT[short] || (BY_SUBDT[short] = [])).push(key)
})
Object.keys(POINTS.districts).forEach(key => {
  const short = key.split(',')[0]
  ;(BY_DISTRICT[short] || (BY_DISTRICT[short] = [])).push(key)
})

// The short names the service-area table already answers. addPoints never
// adds to these: a ตำบล,อำเภอ pair that is unique in the service area must stay
// unique, or an SO address in ปากเกร็ด would start falling back to its zipcode
// the moment the planner loaded a province that reuses the name.
const BASE_SUBDT = Object.assign({}, BY_SUBDT)
const BASE_DISTRICT = Object.assign({}, BY_DISTRICT)
const ADDED = []

// Adds a second table -- src/geoPointsWide.json, every province -- to the
// lookups, so an out-of-area route can be placed. Only the planner calls this
// (planner.worker.js, and plannerClient's fallback); no page bundle loads that
// file. Nothing already held is replaced, so everything that resolved before
// resolves exactly as before: the wide table only places what used to resolve
// to nothing.
function addPoints (table) {
  if (!table || ADDED.indexOf(table) !== -1) return
  ADDED.push(table)

  Object.keys(table.subdistricts || {}).forEach(key => {
    if (POINTS.subdistricts[key]) return
    POINTS.subdistricts[key] = table.subdistricts[key]
    const parts = key.split(',')
    const short = parts[0] + ',' + parts[1]
    if (BASE_SUBDT[short]) return
    ;(BY_SUBDT[short] || (BY_SUBDT[short] = [])).push(key)
  })
  Object.keys(table.districts || {}).forEach(key => {
    if (POINTS.districts[key]) return
    POINTS.districts[key] = table.districts[key]
    const short = key.split(',')[0]
    if (BASE_DISTRICT[short]) return
    ;(BY_DISTRICT[short] || (BY_DISTRICT[short] = [])).push(key)
  })
  Object.keys(table.zipcodes || {}).forEach(zip => {
    if (!POINTS.zipcodes[zip]) POINTS.zipcodes[zip] = table.zipcodes[zip]
  })
}

// The warehouse every route starts and ends at: Kiosk Furniture Co., Ltd.,
// 1/4 หมู่ 4 ถ.บางบัวทอง-สุพรรณบุรี ต.ราษฎร์นิยม อ.ไทรน้อย จ.นนทบุรี 11150 --
// about 40 km north-west of central Bangkok. That position is why the morning
// run into town is the expensive leg: see travelTime.js.
//
// THIS IS THE PLACE PIN, NOT THE MAP CENTRE, and the difference is not academic.
// A Google Maps link carries both: "@14.0607699,100.3273864,17z" is where the
// viewport happens to sit, and "!3d14.0607647!4d100.3299613" is the business
// itself. This was the first of those, 277 m west of the building, which was
// close enough to route with and far enough that Google labelled every แผนที่
// link with the neighbour's name -- FUTURE SIGN CO.,LTD. When updating this,
// take the 3d/4d pair out of the full URL, never the @ triple.
const DEPOT = { lat: 14.0607647, lng: 100.3299613 }

// A reference point for deciding whether a leg heads INTO Bangkok or out of it,
// which is what makes a rush-hour multiplier apply or not. Only the sign of the
// comparison matters, so a few km either way changes nothing for a leg starting
// 40 km out. Not a claim about where "the centre" is.
const CITY_CENTRE = { lat: 13.7563, lng: 100.5018 }

// Straight-line km -> road km. Roads do not run straight: the real driving
// distance between two Bangkok points is consistently ~30-40% longer than the
// great-circle distance. 1.35 is the middle of that, and carries roughly +/-15%
// error, which is why EV range checks add a safety margin on top rather than
// treating this number as exact.
const ROAD_FACTOR = 1.35

const EARTH_RADIUS_KM = 6371.0088

function toRad (deg) {
  return deg * Math.PI / 180
}

// A point is { lat, lng }. Anything missing either coordinate is not a point --
// `0` is a legitimate coordinate, so this tests for null/undefined/NaN rather
// than falsiness.
function isPoint (p) {
  return !!p &&
    typeof p.lat === 'number' && isFinite(p.lat) &&
    typeof p.lng === 'number' && isFinite(p.lng)
}

function haversineKm (a, b) {
  if (!isPoint(a) || !isPoint(b)) return null
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const la1 = toRad(a.lat)
  const la2 = toRad(b.lat)
  const h = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) * Math.sin(dLng / 2)
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)))
}

// The distance a vehicle actually drives, which is the only one that spends
// fuel or battery.
function roadDistanceKm (a, b) {
  const straight = haversineKm(a, b)
  return straight == null ? null : straight * ROAD_FACTOR
}

function distanceFromDepotKm (p) {
  return roadDistanceKm(DEPOT, p)
}

function pair (arr, precision) {
  return { lat: arr[0], lng: arr[1], precision: precision }
}

const NOWHERE = { lat: null, lng: null, precision: null }

// "ตำบล,อำเภอ,จังหวัด" -- exactly what bookingRules.formatLocation() writes and
// what a bookingSlot document stores in `location`. Falls back through the
// administrative levels rather than giving up, because a booking that can be
// placed to within a district is still worth clustering.
function resolveLocation (location) {
  if (!location || typeof location !== 'string') return NOWHERE

  const parts = location.split(',')
  if (parts.length !== 3) return NOWHERE

  const subDt = parts[0].trim()
  const district = parts[1].trim()
  const province = parts[2].trim()

  const exact = POINTS.subdistricts[[subDt, district, province].join(',')]
  if (exact) return pair(exact, 'subdistrict')

  const byDistrict = POINTS.districts[[district, province].join(',')]
  if (byDistrict) return pair(byDistrict, 'district')

  return NOWHERE
}

// A zipcode is the weakest answer this module gives. The count of ตำบล sharing
// the code travels with it so a caller can tell a precise 10310 (3 ตำบล) from a
// meaningless 73000 (25).
function resolveZipcode (zipcode) {
  const zip = String(zipcode == null ? '' : zipcode).trim()
  const hit = POINTS.zipcodes[zip]
  if (!hit) return NOWHERE
  return { lat: hit[0], lng: hit[1], precision: 'zipcode', subdistrictCount: hit[2] }
}

// The last resort before giving up: read the address line itself.
//
// A booking made against a SAP SO has no ตำบล picker behind it -- SAP hands over
// one free-text line -- so before this existed, an SO without a dropped pin was
// simply unplaceable, and the planner refused perfectly ordinary deliveries to
// ต.พิมลราช and ต.คลองสอง whose coordinates were sitting in geoPoints.json all
// along.
//
// A ตำบล name is only accepted when the ตำบล + อำเภอ pair names exactly ONE
// place in the country. Thailand reuses ตำบล names heavily, so an ambiguous
// pair falls back to the อำเภอ, and that to the zipcode, rather than picking a
// province and sending a driver to the wrong one.
function resolveAddressText (address) {
  const text = String(address == null ? '' : address).trim()
  if (!text) return NOWHERE

  const parsed = parseAddress(text)

  if (parsed.subDt && parsed.district) {
    const keys = BY_SUBDT[parsed.subDt + ',' + parsed.district]
    if (keys && keys.length === 1) return pair(POINTS.subdistricts[keys[0]], 'subdistrict')
  }

  if (parsed.district) {
    const keys = BY_DISTRICT[parsed.district]
    if (keys && keys.length === 1) return pair(POINTS.districts[keys[0]], 'district')
  }

  return resolveZipcode(parsed.zip)
}

// "13.7563, 100.5018" -- the form Google Maps copies, which staff paste into the
// booking form's พิกัด field. Deliberately strict, and deliberately a duplicate
// of bookingRules.parseGeo(): requiring geo.js to import bookingRules would drag
// moment-timezone into every consumer of this module for one 12-line function.
// The two must stay in agreement; check-geo.js asserts that they do.
function parseGeo (str) {
  if (!str || typeof str !== 'string') return null
  const parts = str.trim().split(',')
  if (parts.length !== 2) return null
  if (parts[0].trim() === '' || parts[1].trim() === '') return null
  const lat = Number(parts[0].trim())
  const lng = Number(parts[1].trim())
  if (!isFinite(lat) || !isFinite(lng)) return null
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null
  return { lat: lat, lng: lng }
}

// The entry point the planner uses: give it a bookingSlot document, get the best
// point available for it.
//
// Order matters and is not negotiable. A pin someone actually dropped beats a
// ตำบล centroid, which beats a zipcode mean. Falling to `zipcode` is a signal
// the booking needs a real address before it can be routed with confidence, not
// a normal outcome -- bookingDate.vue makes staff pick a ตำบล, so a booking
// reaching that branch came in through some other path.
function resolveBooking (booking) {
  if (!booking) return NOWHERE

  const pin = parseGeo(booking.geo)
  if (pin) return { lat: pin.lat, lng: pin.lng, precision: 'exact' }

  const byLocation = resolveLocation(booking.location)
  if (byLocation.precision) return byLocation

  const byZip = resolveZipcode(booking.zipcode)
  if (byZip.precision) return byZip

  // Last, because it is a guess at a sentence rather than a field anyone chose.
  return resolveAddressText(booking.address || booking.custAddress)
}

// Is this leg heading into Bangkok, out of it, or neither?
//
// travelTime.js needs this because the congested direction flips at midday:
// mornings jam inbound, evenings jam outbound. Every corridor the delivery team
// named -- แจ้งวัฒนะ, งามวงศ์วาน, วิภาวดี, ราชพฤกษ์, กรุงธนบุรี, ลาดพร้าว -- was
// named "ขาเข้าเมือง", and all of them are radials on the depot's approach path.
//
// `neutral` is the important third answer. A 400 m hop between two stops in the
// same ซอย has no meaningful direction, and charging it a rush multiplier
// because it happened to point at the centre would be noise, not modelling.
const DIRECTION_THRESHOLD_KM = 1.5

function legDirection (from, to) {
  if (!isPoint(from) || !isPoint(to)) return 'neutral'
  const before = haversineKm(from, CITY_CENTRE)
  const after = haversineKm(to, CITY_CENTRE)
  const closer = before - after
  if (Math.abs(closer) < DIRECTION_THRESHOLD_KM) return 'neutral'
  return closer > 0 ? 'inbound' : 'outbound'
}

module.exports = {
  DEPOT,
  CITY_CENTRE,
  ROAD_FACTOR,
  DIRECTION_THRESHOLD_KM,
  isPoint,
  haversineKm,
  roadDistanceKm,
  distanceFromDepotKm,
  resolveLocation,
  resolveZipcode,
  resolveBooking,
  resolveAddressText,
  addPoints,
  parseGeo,
  legDirection,
  POINTS
}
